import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ScienceIcon from '@mui/icons-material/Science';
import moment from 'moment/moment';
import QRCode from 'react-qr-code';
import Barcode from 'react-barcode';
import { useApi } from '../../ContextApi/ContextApi';
import './Template6.css';

const Template6 = ({ reportData, QRurl }) => {
  const [FontStyle, setFontStyles] = useState([]);
  const [elecSigMsg, setElecSigMsg] = useState('');
  const { Comment, isheader } = useApi();

  const currentDateTime = new Date();
  const username = (sessionStorage.getItem('FirstName') || '') + (sessionStorage.getItem('LastName') || '');

  // Helper function to convert UTC/server date strings to local browser time
  const formatLocalTime = (dateInput, formatPattern = 'D MMM YYYY hh:mm A') => {
    if (!dateInput) return '-';

    let formattedInput = dateInput;
    // If the date string lacks a 'Z' or offset indicator, append 'Z' so Moment treats it as UTC
    if (typeof dateInput === 'string' && !dateInput.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(dateInput)) {
      formattedInput = `${dateInput}Z`;
    }

    return moment.utc(formattedInput).local().format(formatPattern);
  };

  useEffect(() => {
    try {
      const fonts = JSON.parse(localStorage.getItem('FontStyle'));
      if (fonts) {
        setFontStyles(fonts);
        if (fonts.electronicSignatureMsg) {
          setElecSigMsg(fonts.electronicSignatureMsg);
        }
      }
    } catch (error) {
      console.error('Error loading FontStyle from localStorage:', error);
    }
  }, []);

  const image = localStorage.getItem('LogoImage');
  const qrCodeValue = QRurl;

  const groupByCategoryAndSubCategory = (data) => {
    return data.reduce((result, item) => {
      const category = item.categorY_CODE;
      const subCategory = item.suB_CATEGORY_CODE;

      if (!result[category]) {
        result[category] = {
          categoryValue: item.categorY_VALUE,
          subCategories: {},
        };
      }

      if (!result[category].subCategories[subCategory]) {
        result[category].subCategories[subCategory] = {
          subCategoryValue: item.suB_CATEGORY_VALUE,
          items: [],
        };
      }

      result[category].subCategories[subCategory].items.push(item);
      return result;
    }, {});
  };

  const groupedData = groupByCategoryAndSubCategory(reportData || []);

  const formatTextWithLineBreaks = (text) => {
    if (!text) return '';
    return text.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < text.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const patient = reportData?.[0];
  const labName = reportData?.find((x) => x.laboratoryName !== '' && x.laboratoryName != null)?.laboratoryName;
  const mrNumber = `MR-${patient?.patientID || ''}-${sessionStorage.getItem('LabID') || ''}`;
  const labNumber = patient?.labNumber || `${moment(currentDateTime).format('YYML-MMDD')}-${patient?.patientID || ''}`;

  const bodyFont = FontStyle?.fontfamily ? FontStyle?.fontfamily : 'Georgia, serif';
  const bodyFontSize = FontStyle?.fontsize ? FontStyle?.fontsize : '11px';

  // Rows with no result/unit/range are treated as inline group labels (e.g. "DLC")
  const isLabelRow = (data) => !data.testResult && !data.unit && !data.normalRange;

  return (
    <>
      {/* ================= HEADER ================= */}
      <div className="t6-header">
        {isheader ? (
          <Grid container alignItems="flex-start" className="t6-header-row">
            <Grid item xs={9}>
              <img
                className="t6-logo"
                src={'data:image/png;base64,' + image}
                alt="Lab Logo"
              />
            </Grid>
            <Grid item xs={3} className="t6-qr-col">
              <QRCode value={qrCodeValue} size={90} />
              <div className="t6-qr-caption">Scan to Verify<br />Report Authenticity</div>
            </Grid>
          </Grid>
        ) : ''}
        <hr className="t6-header-divider" />

        <div className="t6-contact-row">
          <span><LocationOnIcon className="t6-icon" fontSize="inherit" /> {patient?.laboratoryAddress}</span>
          <span><PhoneIcon className="t6-icon" fontSize="inherit" /> {patient?.laboratoryContactNumber}</span>
          <span><EmailIcon className="t6-icon" fontSize="inherit" /> {patient?.laboratoryEmail}</span>
          <span><LanguageIcon className="t6-icon" fontSize="inherit" /> www.{(labName || 'lab').toLowerCase().replace(/\s+/g, '')}.com</span>
        </div>
        <hr className="t6-header-divider t6-header-divider-thin" />
      </div>

      {/* ================= PATIENT INFO + REPORT INFO ================= */}
      <div
        className="t6-info-card"
        style={{ fontFamily: bodyFont, fontSize: bodyFontSize }}
      >
        <Grid container>
          <Grid item xs={6} className="t6-info-col t6-info-col-left">
            <div className="t6-pill">
              <PersonIcon fontSize="small" className="t6-pill-icon" />
              PATIENT INFORMATION
            </div>
            <div className="t6-info-row">
              <span className="t6-label">Patient Name</span><span className="t6-colon">:</span>
              <span className="t6-value">{patient?.patientName}</span>
            </div>
            <div className="t6-info-row">
              <span className="t6-label">Age / Gender</span><span className="t6-colon">:</span>
              <span className="t6-value">{patient?.age || '?'} Years / {patient?.sex}</span>
            </div>
            <div className="t6-info-row">
              <span className="t6-label">Referring Doctor</span><span className="t6-colon">:</span>
              <span className="t6-value">{patient?.referredBy || 'Self'}</span>
            </div>
            <div className="t6-info-row">
              <span className="t6-label">Sample Type</span><span className="t6-colon">:</span>
              <span className="t6-value">{patient?.sampleType || '-'}</span>
            </div>
            <div className="t6-info-row">
              <span className="t6-label">Collection Date</span><span className="t6-colon">:</span>
              <span className="t6-value">
                {patient?.collectedOn ? formatLocalTime(patient.collectedOn) : '-'}
              </span>
            </div>
            <div className="t6-info-row">
              <span className="t6-label">Report Date</span><span className="t6-colon">:</span>
              <span className="t6-value">
                {patient?.reportedOn
                  ? formatLocalTime(patient.reportedOn)
                  : formatLocalTime(patient?.createdAt)}
              </span>
            </div>
          </Grid>

          <Grid item xs={6} className="t6-info-col t6-info-col-right">
            <div className="t6-pill">
              <AssignmentIcon fontSize="small" className="t6-pill-icon" />
              REPORT INFORMATION
            </div>
            <div className="t6-info-row">
              <span className="t6-label">MR Number</span><span className="t6-colon">:</span>
              <span className="t6-value">{mrNumber}</span>
            </div>
            <div className="t6-info-row">
              <span className="t6-label">Lab Number</span><span className="t6-colon">:</span>
              <span className="t6-value">{labNumber}</span>
            </div>
            <div className="t6-info-row t6-barcode-row">
              <span className="t6-label">Barcode</span><span className="t6-colon">:</span>
              <span className="t6-value t6-barcode-value">
                <Barcode
                  value={mrNumber}
                  height={18}
                  width={0.9}
                  fontSize={8}
                  margin={0}
                  displayValue={true}
                />
              </span>
            </div>
            <div className="t6-info-row">
              <span className="t6-label">Registration Date</span><span className="t6-colon">:</span>
              <span className="t6-value">{formatLocalTime(patient?.createdAt)}</span>
            </div>
          </Grid>
        </Grid>
      </div>

      {/* ================= REPORT SECTIONS ================= */}
      <div className="t6-content">
        {Object.keys(groupedData).map((category, index) => {
          const subCategoryKeys = Object.keys(groupedData[category].subCategories);

          if (groupedData[category].categoryValue.toUpperCase() === 'URINE EXAMINATION REPORT') {
            const order = ['PHYSICAL EXAMINATION', 'CHEMICAL EXAMINATION', 'MICROSCOPIC EXAMINATION'];
            subCategoryKeys.sort((a, b) => {
              const subCategoryA = groupedData[category].subCategories[a].subCategoryValue.toUpperCase();
              const subCategoryB = groupedData[category].subCategories[b].subCategoryValue.toUpperCase();
              const indexA = order.indexOf(subCategoryA);
              const indexB = order.indexOf(subCategoryB);
              return (indexA === -1 ? order.length : indexA) - (indexB === -1 ? order.length : indexB);
            });
          }

          return (
            <div
              key={index}
              className="t6-category-block"
              style={{ fontFamily: bodyFont, fontSize: bodyFontSize }}
            >
              <div className="t6-category-title">{groupedData[category].categoryValue}</div>

              {subCategoryKeys.map((subCategory, subIndex) => (
                <div key={subIndex} className="t6-subcategory-block">
                  <div className="t6-subcategory-title">
                    {groupedData[category].subCategories[subCategory].subCategoryValue}
                  </div>

                  <TableContainer className="t6-table-container">
                    <Table size="small" className="t6-table">
                      <TableHead>
                        <TableRow className="t6-table-head-row">
                          <TableCell className="t6-th">Test Description</TableCell>
                          <TableCell className="t6-th">Value(s)</TableCell>
                          <TableCell className="t6-th">Unit(s)</TableCell>
                          <TableCell className="t6-th">Reference Range</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {groupedData[category].subCategories[subCategory].items.map((data, dIndex) => (
                          <React.Fragment key={dIndex}>
                            {isLabelRow(data) ? (
                              <TableRow className="t6-label-row">
                                <TableCell colSpan={4} className="t6-label-cell">{data.testName}</TableCell>
                              </TableRow>
                            ) : (
                              <TableRow className="t6-data-row">
                                <TableCell>{data.testName}</TableCell>
                                <TableCell className="t6-value-cell">{data.testResult}</TableCell>
                                <TableCell>{formatTextWithLineBreaks(data.unit)}</TableCell>
                                <TableCell>{formatTextWithLineBreaks(data.normalRange)}</TableCell>
                              </TableRow>
                            )}
                            {data.comment && (
                              <TableRow>
                                <TableCell colSpan={4} className="t6-comment-cell">
                                  Comment: {data.comment}
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              ))}
            </div>
          );
        })}

        <hr className="t6-header-divider" />

        {Comment && (
          <div className="t6-interpretation-box" style={{ fontFamily: bodyFont }}>
            <span className="t6-interpretation-label">Interpretation:</span> {Comment}
          </div>
        )}
      </div>

      {/* ================= ELECTRONIC SIGNATURE DISCLAIMER ================= */}
      {elecSigMsg && (
        <div className="t6-elec-signature-container" style={{ fontFamily: bodyFont }}>
          <p className="t6-elec-signature-text">{elecSigMsg}</p>
        </div>
      )}

      {/* ================= FOOTER ================= */}
      <div className="t6-footer">
        <div className="t6-footer-left">
          <ScienceIcon className="t6-flask-icon" />
          <span className="t6-footer-tagline">Accurate Results&nbsp;&nbsp;Timely Reports&nbsp;&nbsp;Trusted Care</span>
        </div>
        <div className="t6-footer-banner">
          <span>Thank you for choosing</span>
          <strong>{labName || 'Marham Medical Laboratory'}</strong>
        </div>
      </div>

      <div className="t6-footer-meta" style={{ fontFamily: bodyFont }}>
        <span>Printed at: &nbsp;{moment().format('DD-MMM-YYYY hh:mm A')}</span>&nbsp;&nbsp;<span>Printed By: {username}</span>
      </div>
    </>
  );
};

export default Template6;